import { apiService } from '../../../../components/api/apiService';
import { authservice } from '../../../../components/api/authservice';
import { logDataModels } from '../dataModelsLogger';
import type { SyntheticUser } from './types';

export type PolicyDirectoryOption = {
  id: string;
  label: string;
  detail?: string;
};

export type PolicyDirectory = {
  users: PolicyDirectoryOption[];
  roles: PolicyDirectoryOption[];
  groups: PolicyDirectoryOption[];
  simUsers: SyntheticUser[];
};

export const EMPTY_POLICY_DIRECTORY: PolicyDirectory = {
  users: [],
  roles: [],
  groups: [],
  simUsers: [],
};

let cached: PolicyDirectory | null = null;
let inflight: Promise<PolicyDirectory> | null = null;

function asString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : value == null ? '' : String(value).trim();
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

function asList(value: unknown): Record<string, unknown>[] {
  if (Array.isArray(value)) return value.filter((item) => item && typeof item === 'object') as Record<string, unknown>[];
  const container = asRecord(value);
  if (!container) return [];
  if (Array.isArray(container.data)) return asList(container.data);
  if (Array.isArray(container.items)) return asList(container.items);
  if (Array.isArray(container.users)) return asList(container.users);
  if (Array.isArray(container.roles)) return asList(container.roles);
  if (Array.isArray(container.departments)) return asList(container.departments);
  return [];
}

function displayName(row: Record<string, unknown>): string {
  const parts = [asString(row.firstName || row.first_name), asString(row.lastName || row.last_name)].filter(Boolean);
  return parts.join(' ') || asString(row.email) || asString(row.id || row.userId);
}

function uniqueOptions(options: PolicyDirectoryOption[]): PolicyDirectoryOption[] {
  const seen = new Set<string>();
  return options.filter((option) => {
    if (!option.id || seen.has(option.id)) return false;
    seen.add(option.id);
    return true;
  });
}

function departmentFromUser(row: Record<string, unknown>): { id: string; name: string } {
  const nested = asRecord(row.department);
  if (nested) {
    return {
      id: asString(nested.id || nested.departmentId || nested.department_id),
      name: asString(nested.name || nested.departmentName || nested.department_name),
    };
  }
  return {
    id: asString(row.departmentId || row.department_id || row.departmentID || row.DepartmentId),
    name: asString(row.departmentName || row.department_name || (typeof row.department === 'string' ? row.department : '')),
  };
}

type ParsedUser = {
  id: string;
  name: string;
  email: string;
  roleId: string;
  roleName: string;
  departmentId: string;
  departmentName: string;
  tenantId: string;
  isActive: boolean;
};

function parseUsers(raw: unknown): ParsedUser[] {
  return asList(raw)
    .map((row) => {
      const id = asString(row.id || row.userId || row.userID || row.user_id);
      if (!id) return null;
      const department = departmentFromUser(row);
      return {
        id,
        name: displayName(row),
        email: asString(row.email),
        roleId: asString(row.roleID || row.roleId || row.role_id || row.RoleID),
        roleName: asString(row.roleName || row.role_name),
        departmentId: department.id,
        departmentName: department.name,
        tenantId: asString(row.tenantId || row.tenant_id),
        isActive: row.isActive !== false && row.isactive !== false,
      } satisfies ParsedUser;
    })
    .filter((row): row is ParsedUser => Boolean(row?.isActive));
}

function parseRoles(raw: unknown): PolicyDirectoryOption[] {
  const options: PolicyDirectoryOption[] = [];
  asList(raw).forEach((row) => {
    const name = asString(row.roleName || row.role_name || row.name);
    const id = name || asString(row.roleId || row.role_id || row.id);
    if (!id) return;
    const assigned = Array.isArray(row.assignedUsers) ? row.assignedUsers.length : 0;
    const total = Number(row.totalUsers ?? assigned);
    const option: PolicyDirectoryOption = { id, label: name || id };
    if (Number.isFinite(total) && total > 0) {
      option.detail = `${total} ${total === 1 ? 'person' : 'people'}`;
    }
    options.push(option);
  });
  return uniqueOptions(options);
}

function parseGroups(raw: unknown): Array<PolicyDirectoryOption & { matchIds: string[] }> {
  const options = asList(raw)
    .map((row) => {
      const departmentId = asString(row.department_id || row.departmentId);
      const rowId = asString(row.id);
      const name = asString(row.department_name || row.departmentName || row.name);
      const id = name || departmentId || rowId;
      if (!id) return null;
      return {
        id,
        label: name || id,
        matchIds: Array.from(new Set([id, name, departmentId, rowId].filter(Boolean))),
      };
    })
    .filter((row): row is PolicyDirectoryOption & { matchIds: string[] } => Boolean(row));
  const seen = new Set<string>();
  return options.filter((option) => {
    if (seen.has(option.id)) return false;
    seen.add(option.id);
    return true;
  });
}

async function settledValue(label: string, task: Promise<unknown>): Promise<unknown> {
  try {
    return await task;
  } catch (err) {
    logDataModels('warning', 'policy_directory_source_failed', {
      source: label,
      message: err instanceof Error ? err.message : String(err),
    });
    return [];
  }
}

function toSimUser(user: ParsedUser, roleName: string, departmentName: string): SyntheticUser {
  const role = roleName || 'User';
  return {
    id: user.id,
    name: user.name,
    email: user.email,
    role,
    roles: roleName ? [roleName] : [],
    groups: departmentName ? [departmentName] : [],
    department: departmentName,
    region: '',
    tenantId: user.tenantId,
    employeeId: user.id,
    clearance: '',
    channel: 'portal',
  };
}

function buildDirectory(usersRaw: unknown, rolesRaw: unknown, departmentsRaw: unknown): PolicyDirectory {
  const parsedUsers = parseUsers(usersRaw);
  const roles = parseRoles(rolesRaw);
  const groups = parseGroups(departmentsRaw);
  const roleNameById = new Map(
    asList(rolesRaw).map((row) => [
      asString(row.roleId || row.role_id || row.id),
      asString(row.roleName || row.role_name || row.name),
    ]),
  );
  const departmentNameById = new Map<string, string>();
  groups.forEach((group) => {
    group.matchIds.forEach((matchId) => departmentNameById.set(matchId, group.label));
  });

  const users = uniqueOptions(
    parsedUsers.map((user) => {
      const roleName = user.roleName || roleNameById.get(user.roleId) || '';
      const detail = [roleName, user.email].filter(Boolean).join(' · ');
      const option: PolicyDirectoryOption = { id: user.id, label: user.name };
      if (detail) option.detail = detail;
      return option;
    }),
  );

  const simUsers = parsedUsers.map((user) => {
    const roleName = user.roleName || roleNameById.get(user.roleId) || '';
    const departmentName = user.departmentName || departmentNameById.get(user.departmentId) || '';
    return toSimUser(user, roleName, departmentName);
  });

  return {
    users,
    roles,
    groups: groups.map(({ id, label }) => ({ id, label })),
    simUsers,
  };
}

export function getCachedPolicyDirectory(): PolicyDirectory | null {
  return cached;
}

export function findDirectoryUser(id: string): SyntheticUser | undefined {
  if (!id) return undefined;
  return cached?.simUsers.find((user) => user.id === id);
}

export function resolveDirectoryUserId(current: string, simUsers: SyntheticUser[]): string {
  if (current && simUsers.some((user) => user.id === current)) return current;
  return simUsers[0]?.id ?? '';
}

export async function loadPolicyDirectory(): Promise<PolicyDirectory> {
  if (cached) return cached;
  if (inflight) return inflight;
  logDataModels('app', 'policy_directory_load', {});
  inflight = (async () => {
    const [usersRaw, rolesRaw, departmentsRaw] = await Promise.all([
      settledValue('users', authservice.getUsers()),
      settledValue('roles', authservice.getRoles()),
      settledValue('departments', apiService.get('/api/MasterData/departments', {}, 'backend')),
    ]);
    const directory = buildDirectory(usersRaw, rolesRaw, departmentsRaw);
    const empty = !directory.users.length && !directory.roles.length && !directory.groups.length;
    if (!empty) cached = directory;
    if (empty) {
      logDataModels('error', 'policy_directory_failed', { users: 0, roles: 0, groups: 0 });
    } else {
      logDataModels('app', 'policy_directory_loaded', {
        users: directory.users.length,
        roles: directory.roles.length,
        groups: directory.groups.length,
      });
    }
    return directory;
  })();
  try {
    return await inflight;
  } catch (err) {
    logDataModels('error', 'policy_directory_failed', {
      message: err instanceof Error ? err.message : String(err),
    });
    cached = EMPTY_POLICY_DIRECTORY;
    return EMPTY_POLICY_DIRECTORY;
  } finally {
    inflight = null;
  }
}
